import { Avatar, Box, Stack, Typography } from '@mui/material';
import {
  AppNotification,
  NotificationKind,
} from 'components/views/company/outreach-template/proposals/types';
import { FC } from 'react';
import { useNavigate } from 'react-router';

interface NotificationRowProps {
  notification: AppNotification;
  onClick: () => void;
}

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

interface VerbStyle {
  verb: string;
  color: string;
}

const verbStyleFor = (kind: NotificationKind): VerbStyle => {
  switch (kind) {
    case NotificationKind.ProposalSubmitted:
      return { verb: 'submitted', color: 'text.primary' };
    case NotificationKind.ProposalUpdated:
      return { verb: 'updated', color: 'text.primary' };
    case NotificationKind.ProposalApproved:
      return { verb: 'approved', color: 'success.dark' };
    case NotificationKind.ProposalApplied:
      return { verb: 'applied', color: 'success.dark' };
    case NotificationKind.ProposalRejected:
      return { verb: 'rejected', color: 'secondary.main' };
    case NotificationKind.ProposalRemoved:
      return { verb: 'removed', color: 'secondary.main' };
    case NotificationKind.ProposalStale:
      return { verb: 'made stale', color: 'text.primary' };
    case NotificationKind.ProposalClosed:
      return { verb: 'closed', color: 'secondary.main' };
    case NotificationKind.CommentAdded:
      return { verb: 'commented on', color: 'text.primary' };
    case NotificationKind.ChatMessage:
      return { verb: 'messaged in', color: 'text.primary' };
    default:
      return { verb: 'updated', color: 'text.primary' };
  }
};

const entityLabelFor = (refType: string): string => {
  if (refType === 'proposal') return 'a proposal';
  if (refType === 'outreach_chat') return 'the campaign chat';
  if (!refType) return 'an item';
  return `a ${refType}`;
};

const formatTimestamp = (iso: string): string => {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const date = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  return `${time} • ${date}`;
};

export const NotificationRow: FC<NotificationRowProps> = ({ notification, onClick }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    onClick();
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const { verb, color: verbColor } = verbStyleFor(notification.kind);
  const entityLabel = entityLabelFor(notification.refType);
  const actorName = notification.actorName || 'Someone';
  const showCommentBody = notification.kind === NotificationKind.CommentAdded;
  // Resolution kinds have a narrative body worth showing inline (e.g.,
  // "approved" + "finalizing changes" hint, "rejected" + reason, "stale" +
  // cause). Submitted/Updated/Removed have redundant bodies so we skip them.
  const showResolutionBody =
    !showCommentBody &&
    notification.body &&
    (notification.kind === NotificationKind.ProposalApproved ||
      notification.kind === NotificationKind.ProposalApplied ||
      notification.kind === NotificationKind.ProposalRejected ||
      notification.kind === NotificationKind.ProposalStale);

  return (
    <Box
      onClick={handleClick}
      onKeyDown={(e) => {
        // role="button" elements must activate on Enter/Space for keyboard users.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
      sx={{
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        bgcolor: notification.read ? 'transparent' : 'action.hover',
        '&:hover': { bgcolor: 'action.selected' },
      }}
    >
      <Stack direction="row" spacing={1.5}>
        <Avatar sx={{ width: 32, height: 32, fontSize: 12, bgcolor: 'grey.500' }}>
          {initialsFromName(actorName)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              <Box component="span" fontWeight={700}>
                {actorName}
              </Box>{' '}
              <Box component="span" sx={{ color: verbColor }}>
                {verb}
              </Box>{' '}
              <Box component="span" sx={{ color: 'primary.dark', textDecoration: 'underline' }}>
                {entityLabel}
              </Box>
              {notification.companyName && (
                <>
                  {' '}
                  <Box component="span" fontWeight={700}>
                    from {notification.companyName}
                  </Box>
                </>
              )}
            </Typography>
            {!notification.read && (
              <Box
                sx={{
                  flexShrink: 0,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.dark',
                  mt: 0.75,
                }}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(notification.createdAt)}
          </Typography>
          {showCommentBody && notification.body && (
            <Box
              sx={{
                mt: 1,
                px: 1.5,
                py: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {notification.body}
              </Typography>
            </Box>
          )}
          {showResolutionBody && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {notification.body}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
